type NumericRange = { min: number; max: number };

type SignalChartRenderer = (
  canvas: HTMLCanvasElement | null,
  history: number[][],
  options?: { range?: NumericRange; clampToRange?: boolean },
) => void;
type CanvasClearer = (canvas: HTMLCanvasElement | null) => void;
type ActionSeriesProvider = (jointIndex: number) => number[];

type TrainingParameterType = 'number' | 'text' | 'select' | 'checkbox';

const DEFAULT_TRAINING_LEG_LABELS = [
  'Right Front',
  'Right Middle',
  'Right Rear',
  'Left Front',
  'Left Middle',
  'Left Rear',
];
const DEFAULT_JOINTS_PER_LEG = 3;
const RANDOM_SEED_MAX = 1_000_000;
const DEFAULT_RANDOM_SEED = Math.floor(Math.random() * RANDOM_SEED_MAX);
const JOINT_LABELS = ['C1', 'Thigh', 'Tibia'];
interface TrainingChartDescriptor {
  canvas: HTMLCanvasElement;
  jointIndex: number;
}

interface RewardChartDescriptor {
  canvas: HTMLCanvasElement;
  axisTicks: HTMLElement;
}

interface TrainingParameterOption {
  value: string;
  label: string;
}

interface TrainingParameterDefinition {
  id: string;
  label: string;
  type: TrainingParameterType;
  defaultValue: number | string | boolean;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: TrainingParameterOption[];
  hidden?: boolean;
  pairWith?: string;
  pairLabels?: [string, string];
  infoText?: string;
}

export type TrainingParameterValues = Record<string, number | string | boolean>;

const REWARD_OPTIONS: TrainingParameterOption[] = [
  { value: 'forward', label: 'Forward' },
];

export const TRAINING_PARAMETER_DEFS: TrainingParameterDefinition[] = [
  {
    id: 'episodes',
    label: 'Episodes',
    type: 'number',
    defaultValue: 500,
    min: 1,
    step: 1,
    description: 'Number of AGOL episodes (--episodes).',
  },
  {
    id: 'horizon',
    label: 'Horizon',
    type: 'number',
    defaultValue: 56,
    min: 1,
    step: 1,
    description: 'Rollout length (--horizon).',
    infoText: 'Number of simulation timesteps per episode; 56 timesteps is roughly one gait cycle.',
  },
  {
    id: 'initSigma',
    label: 'Initial sigma',
    type: 'number',
    defaultValue: 0.01,
    min: 0,
    step: 0.005,
    description: 'Exploration std (--init-sigma).',
    infoText: 'Starting exploration noise std-dev added to theta/policy weights.',
  },
  {
    id: 'jointRangeMin',
    label: 'Joint clamp min',
    type: 'number',
    defaultValue: -1,
    step: 0.01,
    description: 'Lower clamp (--joint-range-min).',
    infoText: 'Lower joint angle clamp applied to policy outputs.',
    pairWith: 'jointRangeMax',
    pairLabels: ['Min', 'Max'],
  },
  {
    id: 'jointRangeMax',
    label: 'Joint clamp max',
    type: 'number',
    defaultValue: 1,
    step: 0.01,
    description: 'Upper clamp (--joint-range-max).',
    infoText: 'Upper joint angle clamp applied to policy outputs.',
    hidden: true,
  },
  {
    id: 'lrTheta',
    label: 'Theta learning rate',
    type: 'number',
    defaultValue: 1e-2,
    step: 0.001,
    description: 'Parameter LR (--lr-theta).',
    infoText: 'Step size for updating the policy parameters theta.',
  },
  {
    id: 'lrSigma',
    label: 'Sigma learning rate',
    type: 'number',
    defaultValue: 1e-2,
    step: 0.001,
    description: 'Noise LR (--lr-sigma).',
    infoText: 'Step size for updating the noise parameters sigma.',
  },
  {
    id: 'advantageWindow',
    label: 'Buffer window',
    type: 'number',
    defaultValue: 4,
    min: 1,
    step: 1,
    description: 'Finite difference window (--advantage-window).',
    infoText: 'Number of immediate past episodes used for the advantage estimate.',
  },
  {
    id: 'updateEpisodes',
    label: 'Update episodes',
    type: 'number',
    defaultValue: 1,
    min: 1,
    step: 1,
    description: 'Episodes per AGOL update (--update-episodes).',
    infoText: 'Number of immediate past episodes considered for the loss estimate during each update.',
  },
  {
    id: 'seed',
    label: 'Seed',
    type: 'number',
    defaultValue: DEFAULT_RANDOM_SEED,
    step: 1,
    description: 'Random seed (--seed).',
  },
  {
    id: 'reward',
    label: 'Reward mode',
    type: 'select',
    defaultValue: 'forward',
    options: REWARD_OPTIONS,
    description: 'Reward shaping (--reward).',
  },
];

export interface TrainingViewOptions {
  gridElement: HTMLElement | null;
  paramsElement?: HTMLElement | null;
  actuatorNames: readonly string[];
  getActionSeries: ActionSeriesProvider;
  renderSignalChart: SignalChartRenderer;
  clearCanvas: CanvasClearer;
  actionRange?: NumericRange;
  legLabels?: readonly string[];
  jointsPerLeg?: number;
  onParametersChange?: (values: TrainingParameterValues) => void;
}

export class TrainingView {
  private charts: TrainingChartDescriptor[] = [];
  private rewardChart: RewardChartDescriptor | null = null;
  private rewardSeries: number[] | null = null;
  private rewardSeriesLength: number | null = null;

  private readonly gridElement: HTMLElement | null;

  private readonly paramsElement: HTMLElement | null;

  private readonly legLabels: readonly string[];

  private readonly jointsPerLeg: number;

  private parameterValues: TrainingParameterValues = {};

  private episodeSeries: number[][] | null = null;

  private actionRange?: NumericRange;

  constructor(private readonly options: TrainingViewOptions) {
    this.gridElement = options.gridElement;
    this.paramsElement = options.paramsElement ?? null;
    this.legLabels = options.legLabels ?? DEFAULT_TRAINING_LEG_LABELS;
    this.jointsPerLeg = options.jointsPerLeg ?? DEFAULT_JOINTS_PER_LEG;
    this.initializeParameterValues();
    this.actionRange = options.actionRange;
  }

  initialize(): void {
    this.buildGrid();
    this.buildParameterForm();
  }

  render(): void {
    if (this.charts.length) {
      this.charts.forEach(({ canvas, jointIndex }) => {
        const series = this.episodeSeries
          ? this.episodeSeries[jointIndex] ?? []
          : this.options.getActionSeries(jointIndex);
        if (!series.length) {
          this.options.clearCanvas(canvas);
          return;
        }
        const range = this.actionRange ?? this.options.actionRange;
        this.options.renderSignalChart(canvas, [series], { range, clampToRange: true });
      });
    }
    this.renderRewardChart();
  }

  private renderRewardChart(): void {
    const descriptor = this.rewardChart;
    if (!descriptor) {
      return;
    }
    const { canvas } = descriptor;
    if (!this.rewardSeries || this.rewardSeries.length === 0) {
      this.options.clearCanvas(canvas);
      this.updateRewardAxis(descriptor, 0);
      return;
    }
    const range = this.getRewardRange();
    let series = this.rewardSeries;
    let activeLength = series.length;
    if (series && typeof this.rewardSeriesLength === 'number') {
      activeLength = Math.max(1, Math.min(series.length, this.rewardSeriesLength + 1));
      series = series.slice(0, activeLength);
    }
    this.options.renderSignalChart(canvas, [series ?? this.rewardSeries ?? []], { range: range ?? undefined });
    this.updateRewardAxis(descriptor, activeLength);
  }



  private updateRewardAxis(descriptor: RewardChartDescriptor, length: number): void {
    const axis = descriptor.axisTicks;
    if (!axis) {
      return;
    }
    axis.innerHTML = '';
    if (length <= 0) {
      const empty = document.createElement('span');
      empty.className = 'training-reward-axis__empty';
      empty.textContent = 'No rewards yet';
      axis.appendChild(empty);
      return;
    }
    const ticks = this.buildRewardAxisTicks(length);
    ticks.forEach((value) => {
      const tick = document.createElement('span');
      tick.className = 'training-reward-axis__tick';
      tick.textContent = `${value}`;
      const percent = length === 1 ? 0 : (value - 1) / Math.max(1, length - 1);
      tick.style.left = `${percent * 100}%`;
      axis.appendChild(tick);
    });
  }

  private buildRewardAxisTicks(length: number): number[] {
    if (length <= 1) {
      return [1];
    }
    const ticks: number[] = [1];
    const step = length > 50 ? 25 : Math.max(1, Math.round(length / 4));
    for (let value = step; value < length; value += step) {
      ticks.push(Math.min(value, length));
    }
    if (!ticks.includes(length)) {
      ticks.push(length);
    }
    return ticks;
  }

  private getRewardRange(): NumericRange | null {
    if (!this.rewardSeries || this.rewardSeries.length === 0) {
      return null;
    }
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    this.rewardSeries.forEach((value) => {
      if (Number.isFinite(value)) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    });
    if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return null;
      }
      const padding = Math.abs(min) * 0.1 || 1;
      return { min: min - padding, max: max + padding };
    }
    const span = max - min;
    const padding = span * 0.1 || 1;
    return { min: min - padding, max: max + padding };
  }

  getParameters(): TrainingParameterValues {
    return { ...this.parameterValues };
  }

  setEpisodeSeries(series: number[][] | null): void {
    this.episodeSeries = series;
    this.render();
  }

  setRewardSeries(series: number[] | null): void {
    this.rewardSeries = Array.isArray(series) ? [...series] : null;
    this.rewardSeriesLength = null;
    this.render();
  }

  setRewardSeriesLength(length: number | null): void {
    if (typeof length === 'number' && Number.isFinite(length)) {
      this.rewardSeriesLength = Math.max(0, Math.floor(length));
    } else {
      this.rewardSeriesLength = null;
    }
    this.renderRewardChart();
  }

  setActionRange(range?: NumericRange | null): void {
    this.actionRange = range ? { ...range } : undefined;
    this.render();
  }

  private initializeParameterValues(): void {
    this.parameterValues = {};
    TRAINING_PARAMETER_DEFS.forEach((def) => {
      this.parameterValues[def.id] = this.coerceDefaultValue(def);
    });
  }

  private buildGrid(): void {
    if (!(this.gridElement instanceof HTMLElement)) {
      this.charts = [];
      return;
    }
    this.gridElement.innerHTML = '';
    this.charts = [];
    this.rewardChart = null;
    const rewardCard = document.createElement('div');
    rewardCard.className = 'training-chart training-chart--reward';
    const rewardTitle = document.createElement('div');
    rewardTitle.className = 'training-chart__title';
    rewardTitle.textContent = 'Reward';
    const rewardCanvas = document.createElement('canvas');
    rewardCanvas.width = 720;
    rewardCanvas.height = 200;
    const rewardAxis = document.createElement('div');
    rewardAxis.className = 'training-reward-axis';
    const axisTicks = document.createElement('div');
    axisTicks.className = 'training-reward-axis__ticks';
    const axisLabel = document.createElement('div');
    axisLabel.className = 'training-reward-axis__label';
    axisLabel.textContent = 'Episode index';
    rewardAxis.append(axisTicks, axisLabel);
    rewardCard.append(rewardTitle, rewardCanvas, rewardAxis);
    this.gridElement.appendChild(rewardCard);
    this.rewardChart = { canvas: rewardCanvas, axisTicks };
    const rewardDivider = document.createElement('div');
    rewardDivider.className = 'training-chart-separator';
    this.gridElement.appendChild(rewardDivider);
    this.legLabels.forEach((label, legIndex) => {
      const row = document.createElement('div');
      row.className = 'training-grid__row';
      const rowLabel = document.createElement('div');
      rowLabel.className = 'training-grid__label';
      rowLabel.textContent = label;
      const rowCells = document.createElement('div');
      rowCells.className = 'training-grid__cells';
      for (let pose = 0; pose < this.jointsPerLeg; pose += 1) {
        const jointIndex = legIndex * this.jointsPerLeg + pose;
        if (jointIndex >= this.options.actuatorNames.length) {
          continue;
        }
        const card = document.createElement('div');
        card.className = 'training-chart';
        const title = document.createElement('div');
        title.className = 'training-chart__title';
        title.textContent = JOINT_LABELS[pose] ?? `Joint ${pose + 1}`;
        const canvas = document.createElement('canvas');
        canvas.width = 240;
        canvas.height = 120;
        card.append(title, canvas);
        rowCells.appendChild(card);
        this.charts.push({ canvas, jointIndex });
      }
      row.append(rowLabel, rowCells);
      this.gridElement.appendChild(row);
    });
  }

  private buildParameterForm(): void {
    if (!(this.paramsElement instanceof HTMLElement)) {
      return;
    }
    this.paramsElement.innerHTML = '';
    TRAINING_PARAMETER_DEFS.forEach((def) => {
      const field = document.createElement('div');
      field.className = 'training-form__field';
      if (def.type === 'checkbox') {
        field.classList.add('training-form__field--checkbox');
      }
      const control = this.createControl(def);
      const infoElement = this.createInfoElement(def);
      const label = document.createElement('label');
      label.className = 'training-form__label';
      label.htmlFor = def.id;
      label.textContent = def.label;
      if (def.type === 'checkbox') {
        const labelRow = document.createElement('div');
        labelRow.className = 'training-form__label-row';
        labelRow.appendChild(label);
        if (def.id === 'seed' && control instanceof HTMLInputElement) {
          labelRow.appendChild(this.createSeedRefreshButton(def, control));
        }
        if (infoElement) {
          labelRow.appendChild(infoElement);
        }
        field.append(control, labelRow);
      } else {
        const labelRow = document.createElement('div');
        labelRow.className = 'training-form__label-row';
        labelRow.appendChild(label);
        if (def.id === 'seed' && control instanceof HTMLInputElement) {
          labelRow.appendChild(this.createSeedRefreshButton(def, control));
        }
        if (infoElement) {
          labelRow.appendChild(infoElement);
        }
        const wrapper = document.createElement('div');
        wrapper.className = 'training-form__control';
        wrapper.appendChild(control);
        field.append(labelRow);
        field.append(wrapper);
      }
      this.paramsElement.appendChild(field);
    });
  }

  private createControl(
    def: TrainingParameterDefinition,
  ): HTMLInputElement | HTMLSelectElement {
    if (def.type === 'select') {
      const select = document.createElement('select');
      select.id = def.id;
      select.name = def.id;
      def.options?.forEach((option) => {
        const optionNode = document.createElement('option');
        optionNode.value = option.value;
        optionNode.textContent = option.label;
        select.appendChild(optionNode);
      });
      select.value = String(this.parameterValues[def.id]);
      select.addEventListener('change', () => {
        this.setParameterValue(def, select.value);
      });
      return select;
    }
    const input = document.createElement('input');
    input.id = def.id;
    input.name = def.id;
    if (def.type === 'number') {
      input.type = 'number';
      if (typeof def.min === 'number') {
        input.min = `${def.min}`;
      }
      if (typeof def.max === 'number') {
        input.max = `${def.max}`;
      }
      if (typeof def.step === 'number') {
        input.step = `${def.step}`;
      }
      input.value = String(this.parameterValues[def.id]);
      input.addEventListener('input', () => {
        this.setParameterValue(def, input.value);
      });
      return input;
    }
    if (def.type === 'checkbox') {
      input.type = 'checkbox';
      input.checked = Boolean(this.parameterValues[def.id]);
      input.addEventListener('change', () => {
        this.setParameterValue(def, input.checked);
      });
      return input;
    }
    input.type = 'text';
    input.value = String(this.parameterValues[def.id] ?? '');
    input.addEventListener('input', () => {
      this.setParameterValue(def, input.value);
    });
    return input;
  }
  private createInfoElement(def: TrainingParameterDefinition): HTMLDivElement | null {
    if (!def.infoText) {
      return null;
    }
    const tooltipId = `training-parameter-info-${def.id}`;
    const wrapper = document.createElement('div');
    wrapper.className = 'training-form__info-wrapper';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'training-form__info-button';
    button.setAttribute('aria-label', `More info for ${def.label}`);
    button.setAttribute('aria-describedby', tooltipId);
    button.textContent = 'i';
    const tooltip = document.createElement('div');
    tooltip.id = tooltipId;
    tooltip.className = 'training-form__info-tooltip';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.textContent = def.infoText;
    wrapper.append(button, tooltip);
    return wrapper;
  }

  private createSeedRefreshButton(
    def: TrainingParameterDefinition,
    control: HTMLInputElement,
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'training-form__seed-refresh';
    button.setAttribute('aria-label', 'Generate random seed');
    button.title = 'Randomise seed';
    button.textContent = '\u21bb';
    button.addEventListener('click', () => {
      const nextSeed = this.generateRandomSeed();
      control.value = `${nextSeed}`;
      this.setParameterValue(def, nextSeed);
    });
    return button;
  }

  private generateRandomSeed(): number {
    return Math.floor(Math.random() * RANDOM_SEED_MAX);
  }

  private coerceDefaultValue(def: TrainingParameterDefinition): number | string | boolean {
    if (def.type === 'number') {
      return Number(def.defaultValue);
    }
    if (def.type === 'checkbox') {
      return Boolean(def.defaultValue);
    }
    return typeof def.defaultValue === 'string' ? def.defaultValue : '';
  }

  private setParameterValue(
    def: TrainingParameterDefinition,
    rawValue: string | number | boolean,
  ): void {
    let value: number | string | boolean;
    if (def.type === 'number') {
      const numeric = typeof rawValue === 'number' ? rawValue : Number(rawValue);
      value = Number.isFinite(numeric) ? numeric : this.parameterValues[def.id];
    } else if (def.type === 'checkbox') {
      value = Boolean(rawValue);
    } else {
      value = typeof rawValue === 'string' ? rawValue : String(rawValue);
    }
    this.parameterValues[def.id] = value;
    this.options.onParametersChange?.({ ...this.parameterValues });
  }
}

